import type { ParseKeys, TFunction } from 'i18next'

import { canAny, CAP } from '@/lib/can'
import type { Capability, CurrentUser } from '@/types/domain'

/* The Settings tabs, and which capability opens each (S-7, ADVISOR B1).
 *
 * Each tab is gated by the capability that guards the SERVER routes it calls —
 * not by the role's name (roles are runtime records, migration 0021) and not by
 * a `.read` capability, which guards nothing:
 *
 *   users, entities, numbering (and the entity logo) — the `/admin` group,
 *     `admin.users.manage` (cmd/asa/routes.go);
 *   templates (with the thresholds) — their writes under `/admin/settings`,
 *     `admin.settings.manage` (their reads are open to every session, which is
 *     what a quote's picker needs);
 *   route templates — create, update and hide behind `route.template.manage`
 *     OR `admin.settings.manage` (S-10: Operations and Pricing keep the
 *     catalogue the leg page creates templates into); the delete stays on
 *     `admin.settings.manage` alone, and the tab offers it only to that key;
 *   access — the roles editor, `admin.roles.manage`.
 *
 * A tab names the capabilities that open it — ANY of them (`canAny`), so a
 * tab whose routes take either of two keys says both.
 *
 * A tab the reader may not use is absent rather than refused on every press —
 * the same rule as the sidebar (`lib/navigation.ts`). The server re-checks every
 * request regardless (AD-21). Users sit beside roles and access, so the two
 * halves of "who may do what" are one place (A-1). */
export const SETTINGS_TABS = [
  { key: 'entities', labelKey: 'settings.tabs.entities',
    subtitleKey: 'settings.subtitleParts.entities', capabilities: [CAP.adminUsersManage] },
  { key: 'numbering', labelKey: 'settings.tabs.numbering',
    subtitleKey: 'settings.subtitleParts.numbering', capabilities: [CAP.adminUsersManage] },
  { key: 'templates', labelKey: 'settings.tabs.templates',
    subtitleKey: 'settings.subtitleParts.templates', capabilities: [CAP.adminSettingsManage] },
  {
    key: 'directions',
    labelKey: 'settings.tabs.directions',
    subtitleKey: 'settings.subtitleParts.directions',
    capabilities: [CAP.routeTemplateManage, CAP.adminSettingsManage],
  },
  { key: 'users', labelKey: 'settings.tabs.users',
    subtitleKey: 'settings.subtitleParts.users', capabilities: [CAP.adminUsersManage] },
  { key: 'access', labelKey: 'settings.tabs.access',
    subtitleKey: 'settings.subtitleParts.access', capabilities: [CAP.adminRolesManage] },
] as const satisfies ReadonlyArray<{
  key: string
  labelKey: string
  subtitleKey: ParseKeys
  capabilities: readonly Capability[]
}>

export type SettingsTab = (typeof SETTINGS_TABS)[number]
export type SettingsTabKey = SettingsTab['key']

/** The tabs this reader may open, in page order. None without a session. */
export function settingsTabsFor(user: CurrentUser | null | undefined): SettingsTab[] {
  return SETTINGS_TABS.filter((tab) => canAny(user, ...tab.capabilities))
}

/** The tab to render for a requested `?tab=`.
 *
 *  The requested tab when the reader may open it, otherwise the first one they
 *  may; undefined when they may open none. Computed at render and never
 *  written back to the URL: a link to `?tab=users` opened by a reader who
 *  lacks it shows their first tab and keeps the address it was given. */
export function settingsTab(
  user: CurrentUser | null | undefined,
  requested: string | null,
): SettingsTabKey | undefined {
  const permitted = settingsTabsFor(user)
  return (permitted.find((tab) => tab.key === requested) ?? permitted[0])?.key
}

/** The page's subtitle: the tabs this reader may open, named in the reader's
 *  language — «Шаблоны маршрутов.» for a reader whose only tab is that one, not
 *  a sentence promising people, roles and numbering they cannot reach (N-31).
 *
 *  Composed from the same list that draws the tab bar, so the two cannot
 *  disagree, and joined by `Intl.ListFormat` so each language's own "and" and
 *  commas apply. Undefined with no tab: the page then says so itself. */
export function settingsSubtitle(
  tabs: readonly SettingsTab[],
  t: TFunction,
  locale: string,
): string | undefined {
  if (tabs.length === 0) return undefined

  const phrases = tabs.map((tab) => t(tab.subtitleKey))
  const list = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    phrases,
  )
  return `${list.charAt(0).toLocaleUpperCase(locale)}${list.slice(1)}.`
}
